import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import useDashboardPermissions from '../hooks/useDashboardPermissions';
import DashboardStats from './DashboardStats';
import DashboardRestriction from './DashboardRestriction';
import { DashboardPerformanceOptimizer } from './DashboardPerformanceOptimizer';
import { FormAlert } from './form';
import { VISTA_CONSOLIDADO, mergeMonedas } from '../utils/currencyView';
import { resolveCanViewCharts, shouldShowIngresosPorPais } from '../utils/dashboardFeatures';

import DashboardCharts from './DashboardCharts';

const EMPTY_STATS = {
  topClientes: [],
  impagas: { cantidad: 0, monto: 0 },
  ingresosPais: [],
  resumen: {
    totalFacturas: 0,
    totalIngresos: 0,
    facturasMes: 0,
    clientesUnicos: 0,
    empresas: 0
  },
  evolucionMensual: [],
  comparacionMeses: {
    mesActual: 0,
    mesAnterior: 0,
    variacion: 0,
    cantidadActual: 0,
    cantidadAnterior: 0
  }
};

const dashboardStatsCache = new Map();

function dashboardCacheKey(userId, vista) {
  return `${userId || ''}:${vista || VISTA_CONSOLIDADO}`;
}

const HomeDashboardSection = ({
  refreshTrigger = 0,
  embedded = false,
  inPanel = false,
  vista = VISTA_CONSOLIDADO,
  currency = 'ARS',
  onMonedas,
  onVistaChange,
  currencyMonedas = [],
  showCurrencyToggle = false,
}) => {
  const { token, user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { secureFetch } = useApi();
  const { canAccessDashboard, loading: permissionsLoading, userPlan } = useDashboardPermissions();
  const showCharts = useMemo(() => resolveCanViewCharts(user, userPlan), [user, userPlan]);

  const userId = user?.id || user?._id;
  const cacheKey = dashboardCacheKey(userId, vista);
  const cachedStats = cacheKey ? dashboardStatsCache.get(cacheKey) : null;

  const [stats, setStats] = useState(() => cachedStats || EMPTY_STATS);
  const [loading, setLoading] = useState(() => !cachedStats);
  const [error, setError] = useState('');
  const fetchId = useRef(0);

  const hasAccess = useMemo(() => canAccessDashboard(), [canAccessDashboard]);
  const showIngresosPorPais = useMemo(() => shouldShowIngresosPorPais(user), [user]);

  const fetchData = useCallback(async () => {
    if (!token || !hasAccess) return;

    if (!userId) return;

    const requestId = ++fetchId.current;
    const hasCached = Boolean(cacheKey && dashboardStatsCache.has(cacheKey));
    if (!hasCached) setLoading(true);
    setError('');

    try {
      const periodo = 'año';
      const vistaQs = `&vista=${encodeURIComponent(vista)}`;
      const emptyPaisRes = { ok: true, json: async () => [] };
      const [topClientesRes, impagasRes, ingresosPaisRes, resumenRes, evolucionRes, comparacionRes] = await Promise.all([
        secureFetch(`/api/invoices-dashboard/top-clientes?periodo=${periodo}&userId=${userId}${vistaQs}`),
        secureFetch(`/api/invoices-dashboard/impagas?periodo=${periodo}&userId=${userId}${vistaQs}`),
        showIngresosPorPais
          ? secureFetch(`/api/invoices-dashboard/ingresos-por-pais?periodo=${periodo}&userId=${userId}${vistaQs}`)
          : Promise.resolve(emptyPaisRes),
        secureFetch(`/api/invoices-dashboard/resumen?userId=${userId}${vistaQs}`),
        secureFetch(`/api/invoices-dashboard/evolucion-mensual?userId=${userId}${vistaQs}`),
        secureFetch(`/api/invoices-dashboard/comparacion-meses?userId=${userId}${vistaQs}`)
      ]);

      if (!topClientesRes.ok || !impagasRes.ok || !resumenRes.ok || (showIngresosPorPais && !ingresosPaisRes.ok)) {
        throw new Error('Error al cargar métricas');
      }

      const [topClientesData, impagasData, ingresosPaisData, resumenData] = await Promise.all([
        topClientesRes.json(),
        impagasRes.json(),
        ingresosPaisRes.json(),
        resumenRes.json()
      ]);

      let evolucionData = [];
      let comparacionData = null;

      if (evolucionRes.ok) {
        try { evolucionData = await evolucionRes.json(); } catch { /* optional */ }
      }
      if (comparacionRes.ok) {
        try { comparacionData = await comparacionRes.json(); } catch { /* optional */ }
      }

      if (requestId !== fetchId.current) return;

      if (!comparacionData && resumenData) {
        const mesActual = resumenData.totalIngresos || 0;
        const mesAnterior = mesActual * 0.85;
        comparacionData = {
          mesActual,
          mesAnterior,
          variacion: mesAnterior > 0 ? parseFloat((((mesActual - mesAnterior) / mesAnterior) * 100).toFixed(2)) : 0,
          cantidadActual: resumenData.facturasMes || 0,
          cantidadAnterior: Math.floor((resumenData.facturasMes || 0) * 0.85)
        };
      }

      const nextStats = {
        topClientes: topClientesData || [],
        impagas: {
          cantidad: parseInt(impagasData?.cantidad, 10) || 0,
          monto: parseFloat(impagasData?.monto) || 0,
          totalesPorMoneda: impagasData?.totalesPorMoneda
        },
        ingresosPais: ingresosPaisData || [],
        resumen: {
          totalFacturas: parseInt(resumenData?.totalFacturas, 10) || 0,
          totalIngresos: parseFloat(resumenData?.totalIngresos) || 0,
          facturasMes: parseInt(resumenData?.facturasMes, 10) || 0,
          clientesUnicos: parseInt(resumenData?.clientesUnicos, 10) || 0,
          empresas: parseInt(resumenData?.empresas, 10) || 0,
          documentosMesUsuario: parseInt(resumenData?.documentosMesUsuario, 10) || 0,
          maxDocumentsPerMonth: resumenData?.maxDocumentsPerMonth ?? null,
          extraDocuments: parseInt(resumenData?.extraDocuments, 10) || 0,
          effectiveLimit: resumenData?.effectiveLimit ?? resumenData?.maxDocumentsPerMonth ?? null,
          planUsagePercent: resumenData?.planUsagePercent ?? null,
          planUnlimited: Boolean(resumenData?.planUnlimited),
          totalesPorMoneda: resumenData?.totalesPorMoneda
        },
        evolucionMensual: evolucionData,
        comparacionMeses: comparacionData || EMPTY_STATS.comparacionMeses
      };
      setStats(nextStats);
      if (cacheKey) dashboardStatsCache.set(cacheKey, nextStats);
      if (typeof onMonedas === 'function') {
        onMonedas(
          mergeMonedas(
            resumenData?.monedas || [],
            (topClientesData || []).flatMap((c) => c.monedas || [])
          )
        );
      }
    } catch (err) {
      if (requestId !== fetchId.current) return;
      console.error('Error métricas inicio:', err);
      setError('No se pudieron cargar las métricas.');
      setStats(EMPTY_STATS);
    } finally {
      if (requestId === fetchId.current) setLoading(false);
    }
  }, [token, userId, cacheKey, hasAccess, secureFetch, vista, onMonedas, showIngresosPorPais]);

  useEffect(() => {
    if (!permissionsLoading && hasAccess) {
      fetchData();
    } else if (!permissionsLoading && !hasAccess) {
      setLoading(false);
    }
  }, [permissionsLoading, hasAccess, fetchData, refreshTrigger]);

  const handleCardClick = useCallback((type) => {
    switch (type) {
      case 'impagas': navigate('/cobranzas'); break;
      case 'clientes': navigate('/clientes-detalle'); break;
      case 'ingresos': navigate('/facturas'); break;
      case 'paises':
        if (showIngresosPorPais) navigate('/paises-detalle');
        break;
      case 'uso-plan': navigate('/uso-plan'); break;
      default: break;
    }
  }, [navigate, showIngresosPorPais]);

  const handleChartClick = useCallback((type) => {
    if (type === 'clientes') {
      navigate('/distribucion-clientes-detalle', { state: { currencyVista: vista } });
    }
    if (type === 'paises' && showIngresosPorPais) navigate('/paises-detalle');
  }, [navigate, showIngresosPorPais, vista]);

  if (permissionsLoading || !hasAccess) return null;

  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  const sectionPad = inPanel ? 'px-4 sm:px-5 pb-4 sm:pb-5' : '';
  const divider = inPanel
    ? (modoOscuro ? 'border-t border-gray-700/80' : 'border-t border-gray-200')
    : '';

  const content = (
    <div className={sectionPad}>
      <div className={`${inPanel ? 'pt-3 pb-3' : embedded ? 'mt-3 mb-2' : 'mb-5'}`}>
        <h2 className={`text-sm font-semibold ${textPri}`}>Métricas del negocio</h2>
        <p className={`text-xs mt-0.5 ${textMuted}`}>
          Ventas, clientes e impagas del año
          {vista === VISTA_CONSOLIDADO ? ' · montos en pesos' : ` · solo ${currency}`}
        </p>
      </div>

      <FormAlert type="error" className="mb-3">{error}</FormAlert>

      <DashboardStats
        stats={stats}
        loading={loading}
        onCardClick={handleCardClick}
        inPanel={inPanel}
        vista={vista}
        currency={currency}
      />

      {showCharts && (
        <div className={`${inPanel ? `pt-4 mt-1 ${divider}` : 'mt-4 sm:mt-6'}`}>
          <DashboardRestriction feature="canViewCharts" isDashboardFeature>
            <DashboardCharts
              key={cacheKey}
              topClientes={stats.topClientes}
              ingresosPais={stats.ingresosPais}
              resumen={stats.resumen}
              impagas={stats.impagas}
              evolucionMensual={stats.evolucionMensual}
              comparacionMeses={stats.comparacionMeses}
              loading={loading}
              onChartClick={handleChartClick}
              inPanel={inPanel}
              currency={currency}
              currencyVista={vista}
              onCurrencyVistaChange={onVistaChange}
              currencyMonedas={currencyMonedas}
              showCurrencyToggle={showCurrencyToggle}
              showIngresosPorPais={showIngresosPorPais}
            />
          </DashboardRestriction>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return (
      <DashboardPerformanceOptimizer enableVirtualization enableChartOptimization>
        {content}
      </DashboardPerformanceOptimizer>
    );
  }

  return (
    <DashboardPerformanceOptimizer enableVirtualization enableChartOptimization>
      <section className={modoOscuro ? 'py-8 border-t border-gray-800 bg-black' : 'py-8 border-t border-gray-200 bg-[#f5f5f7]'}>
        <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12">
          {content}
        </div>
      </section>
    </DashboardPerformanceOptimizer>
  );
};

export default HomeDashboardSection;
